import React, { useEffect, useRef, useState, useCallback } from "react";
import config from "../config";

// ─── Priority / Status colors ──────────────────────────────────────────────────
const PRIORITY_COLOR = {
  Critical: { dot: "bg-red-500",    badge: "bg-red-500/15 text-red-400 border-red-500/30" },
  High:     { dot: "bg-orange-500", badge: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  Medium:   { dot: "bg-yellow-400", badge: "bg-yellow-400/15 text-yellow-300 border-yellow-400/30" },
  Low:      { dot: "bg-green-500",  badge: "bg-green-500/15 text-green-400 border-green-500/30" },
};

const STATUS_COLOR = {
  Open:          "bg-blue-500/15 text-blue-400 border-blue-500/30",
  Pending:       "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  Resolved:      "bg-green-500/15 text-green-400 border-green-500/30",
  Closed:        "bg-gray-500/15 text-gray-400 border-gray-500/30",
  "In Progress": "bg-purple-500/15 text-purple-400 border-purple-500/30",
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-PH", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const priorityOf = (p) =>
  PRIORITY_COLOR[p] || { dot: "bg-gray-400", badge: "bg-gray-500/15 text-gray-400 border-gray-500/30" };
const statusOf = (s) => STATUS_COLOR[s] || "bg-gray-500/15 text-gray-400 border-gray-500/30";

// ─── Base64 helpers ────────────────────────────────────────────────────────────
const detectMimeType = (base64) => {
  if (!base64) return null;
  const str = base64.trim();
  if (str.startsWith("data:")) return str;
  if (str.startsWith("/9j/"))  return `data:image/jpeg;base64,${str}`;
  if (str.startsWith("iVBOR")) return `data:image/png;base64,${str}`;
  if (str.startsWith("R0lGO")) return `data:image/gif;base64,${str}`;
  if (str.startsWith("UklGR")) return `data:image/webp;base64,${str}`;
  if (str.startsWith("JVBER")) return `data:application/pdf;base64,${str}`;
  return `data:image/jpeg;base64,${str}`;
};

const isImageDataUri = (uri) => !!uri && uri.startsWith("data:image/");
const isPdfDataUri   = (uri) => !!uri && uri.startsWith("data:application/pdf");

const downloadBase64 = (dataUri, filename) => {
  const a = document.createElement("a");
  a.href = dataUri;
  a.download = filename || "attachment";
  a.click();
};

// ─── Cache para hindi paulit-ulit mag-fetch ───────────────────────────────────
const avatarCache = {};

// ─── Hook: fetch user/admin profile picture (returns blob URL) ────────────────
const useAvatar = (username, isAdmin = false) => {
  const [src, setSrc] = useState(avatarCache[username] || null);

  useEffect(() => {
    if (!username) return;
    if (avatarCache[username]) { setSrc(avatarCache[username]); return; }

    const endpoint = isAdmin
      ? `${config.API_BASE_URL}/Admin/get-profile-picture/${username}`
      : `${config.API_BASE_URL}/Users/get-profile-picture/${username}`;

    fetch(endpoint, { headers: { accept: "*/*" } })
      .then(async (res) => {
        if (!res.ok || res.status === 204) return;
        const ct = res.headers.get("content-type") || "";
        if (ct.startsWith("image/")) {
          // ── API returns raw image ──
          const blob = await res.blob();
          const url  = URL.createObjectURL(blob);
          avatarCache[username] = url;
          setSrc(url);
        } else {
          // ── API returns JSON with base64 field ──
          const data = await res.json();
          const pic  = data?.profilePicture ?? data?.ProfilePicture ??
                       data?.picture ?? data?.photo ?? data?.avatar ?? data?.image ?? null;
          if (pic) {
            const url = detectMimeType(pic);
            avatarCache[username] = url;
            setSrc(url);
          }
        }
      })
      .catch(() => {});
  }, [username, isAdmin]);

  return src;
};

// ─── Avatar circle component ──────────────────────────────────────────────────
const Avatar = ({ username, isAdmin = false, size = 40, initial = "?" }) => {
  const src = useAvatar(username, isAdmin);

  return (
    <div
      className="rounded-full overflow-hidden flex-shrink-0 ring-2 ring-gray-700"
      style={{ width: size, height: size }}
    >
      {src ? (
        <img src={src} alt={username} className="w-full h-full object-cover" />
      ) : (
        <div
          className="w-full h-full flex items-center justify-center font-bold text-white select-none"
          style={{
            fontSize: size * 0.38,
            background: isAdmin
              ? "linear-gradient(135deg,#b45309,#d97706)"
              : "linear-gradient(135deg,#4f46e5,#7c3aed)",
          }}
        >
          {(initial || username || "?").charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
};

// ─── Lightbox ─────────────────────────────────────────────────────────────────
const Lightbox = ({ src, onClose }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="relative max-w-5xl max-h-[90vh] flex flex-col items-center">
        <img src={src} alt="Attachment"
          className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl border border-gray-700" />
        <div className="flex gap-3 mt-4">
          <button onClick={() => downloadBase64(src, "attachment.jpg")}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition font-semibold">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition">Close</button>
        </div>
      </div>
      <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-full transition">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ─── Attachment Preview ────────────────────────────────────────────────────────
const AttachmentPreview = ({ base64 }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  if (!base64) return null;
  const dataUri = detectMimeType(base64);
  const isImage = isImageDataUri(dataUri);
  const isPdf   = isPdfDataUri(dataUri);

  return (
    <div>
      <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-2 font-semibold">📎 Attachment</p>
      {isImage ? (
        <div className="relative group w-fit">
          <img src={dataUri} alt="Attachment"
            className="max-h-48 max-w-full rounded-xl border border-gray-600 shadow-lg object-cover cursor-pointer group-hover:brightness-75 transition-all duration-200"
            onClick={() => setLightboxOpen(true)} />
          <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-xl"
            onClick={() => setLightboxOpen(true)}>
            <div className="bg-black/60 rounded-lg px-3 py-1.5 flex items-center gap-1.5">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
              <span className="text-white text-xs font-semibold">View Full</span>
            </div>
          </div>
          <button onClick={() => downloadBase64(dataUri, "image.jpg")}
            className="absolute top-2 right-2 p-1.5 bg-gray-900/80 hover:bg-gray-900 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
        </div>
      ) : isPdf ? (
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 w-fit">
          <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-red-400 text-xs font-bold">PDF</span>
          </div>
          <div>
            <p className="text-sm text-gray-200 font-medium">PDF Document</p>
            <p className="text-xs text-gray-500">Click to download</p>
          </div>
          <button onClick={() => downloadBase64(dataUri, "attachment.pdf")}
            className="ml-2 p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-gray-700/40 border border-gray-600/40 rounded-xl px-4 py-3 w-fit">
          <div className="w-10 h-10 bg-gray-600/50 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <p className="text-sm text-gray-200 font-medium">Attachment</p>
            <p className="text-xs text-gray-500">Click to download</p>
          </div>
          <button onClick={() => downloadBase64(dataUri, "attachment")}
            className="ml-2 p-2 bg-gray-600/50 hover:bg-gray-600 text-gray-300 rounded-lg transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
        </div>
      )}
      {lightboxOpen && isImage && <Lightbox src={dataUri} onClose={() => setLightboxOpen(false)} />}
    </div>
  );
};

// ─── Detail Row ────────────────────────────────────────────────────────────────
const DetailRow = ({ label, value, full }) => {
  if (!value) return null;
  return (
    <div className={full ? "col-span-2" : ""}>
      <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-0.5 font-semibold">{label}</p>
      <p className="text-sm text-gray-200 break-words">{value}</p>
    </div>
  );
};

// ─── Ticket Modal ──────────────────────────────────────────────────────────────
const TicketModal = ({ ticket, onClose }) => {
  const p = priorityOf(ticket.priority);
  // requester username — try userId or name
  const requesterUsername = ticket.userId || ticket.name || null;

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-700 flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs font-mono text-indigo-400 bg-indigo-500/10 border border-indigo-500/30 px-2 py-0.5 rounded">
                {ticket.ticketNumber || "—"}
              </span>
              {ticket.status && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${statusOf(ticket.status)}`}>{ticket.status}</span>}
              {ticket.priority && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${p.badge}`}>{ticket.priority}</span>}
            </div>
            <h2 className="text-base font-bold text-white leading-snug">{ticket.subject || "(No Subject)"}</h2>
          </div>
          <button onClick={onClose} className="flex-shrink-0 p-1.5 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {ticket.attachment && <AttachmentPreview base64={ticket.attachment} />}
          {ticket.description && (
            <div>
              <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5 font-semibold">Description</p>
              <div className="bg-gray-700/50 rounded-lg px-4 py-3 text-sm text-gray-300 leading-relaxed whitespace-pre-wrap border border-gray-600/50">{ticket.description}</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <DetailRow label="Category"   value={ticket.category} />
            <DetailRow label="Issue Type" value={ticket.issueType} />
            <DetailRow label="Origin"     value={ticket.origin} />
            <DetailRow label="Severity"   value={ticket.severity} />
            <DetailRow label="Urgency"    value={ticket.urgency} />
            <DetailRow label="Team"       value={ticket.team} />
            <DetailRow label="Assignees"  value={ticket.assignees} />
            <DetailRow label="Reporter"   value={ticket.reporter} />
            <DetailRow label="Components" value={ticket.components} />
            <DetailRow label="Affected"   value={ticket.affected} />
            <DetailRow label="Summary"    value={ticket.summary} full />
          </div>
          {(ticket.name || ticket.contact || ticket.email || ticket.userId) && (
            <div>
              <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-2 font-semibold">Requester Info</p>
              <div className="flex items-center gap-3 bg-gray-700/30 rounded-lg px-4 py-3 border border-gray-600/40 mb-3">
                <Avatar username={requesterUsername} isAdmin={false} size={44} initial={ticket.name || ticket.userId} />
                <div>
                  <p className="text-sm font-semibold text-white">{ticket.name || ticket.userId || "—"}</p>
                  {ticket.email && <p className="text-xs text-gray-400">{ticket.email}</p>}
                  {ticket.contact && <p className="text-xs text-gray-400">📞 {ticket.contact}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 bg-gray-700/30 rounded-lg px-4 py-3 border border-gray-600/40">
                <DetailRow label="Name"    value={ticket.name} />
                <DetailRow label="Contact" value={ticket.contact} />
                <DetailRow label="Email"   value={ticket.email} />
                <DetailRow label="User ID" value={ticket.userId} />
              </div>
            </div>
          )}
          <div>
            <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-2 font-semibold">Dates & Time</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 bg-gray-700/30 rounded-lg px-4 py-3 border border-gray-600/40">
              <DetailRow label="Created At"     value={formatDate(ticket.createdAt)} />
              <DetailRow label="Date Started"   value={formatDate(ticket.dateStarted)} />
              <DetailRow label="Date Updated"   value={formatDate(ticket.dateUpdated)} />
              <DetailRow label="Deadline"       value={formatDate(ticket.deadline)} />
              <DetailRow label="Time Goal"      value={ticket.timeGoal} />
              <DetailRow label="Time Spent"     value={ticket.timeSpent} />
              <DetailRow label="Time Remaining" value={ticket.timeRemaining} />
            </div>
          </div>
          {(ticket.workDescription || ticket.notes || ticket.internalNotes) && (
            <div>
              <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-2 font-semibold">Work Details</p>
              <div className="space-y-3">
                {ticket.workDescription && <div><p className="text-[11px] text-gray-500 mb-1">Work Description</p><div className="bg-gray-700/50 rounded-lg px-4 py-3 text-sm text-gray-300 border border-gray-600/50 whitespace-pre-wrap">{ticket.workDescription}</div></div>}
                {ticket.notes && <div><p className="text-[11px] text-gray-500 mb-1">Notes</p><div className="bg-gray-700/50 rounded-lg px-4 py-3 text-sm text-gray-300 border border-gray-600/50 whitespace-pre-wrap">{ticket.notes}</div></div>}
                {ticket.internalNotes && <div><p className="text-[11px] text-gray-500 mb-1">Internal Notes</p><div className="bg-yellow-500/5 rounded-lg px-4 py-3 text-sm text-gray-300 border border-yellow-500/20 whitespace-pre-wrap">{ticket.internalNotes}</div></div>}
              </div>
            </div>
          )}
          {ticket.tags && (
            <div>
              <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5 font-semibold">Tags</p>
              <div className="flex flex-wrap gap-1">
                {ticket.tags.split(",").map((tag, i) => (
                  <span key={i} className="text-[11px] bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 px-2 py-0.5 rounded-full">{tag.trim()}</span>
                ))}
              </div>
            </div>
          )}
          {(ticket.openDescription || ticket.pendingDescription || ticket.completeDescription || ticket.resolvedDescription || ticket.closeDescription) && (
            <div>
              <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-2 font-semibold">Status Descriptions</p>
              <div className="space-y-2">
                {ticket.openDescription     && <div className="text-xs text-gray-400 bg-gray-700/30 rounded px-3 py-2 border-l-2 border-blue-500"><span className="font-semibold text-blue-400">Open: </span>{ticket.openDescription}</div>}
                {ticket.pendingDescription  && <div className="text-xs text-gray-400 bg-gray-700/30 rounded px-3 py-2 border-l-2 border-yellow-500"><span className="font-semibold text-yellow-400">Pending: </span>{ticket.pendingDescription}</div>}
                {ticket.completeDescription && <div className="text-xs text-gray-400 bg-gray-700/30 rounded px-3 py-2 border-l-2 border-green-500"><span className="font-semibold text-green-400">Complete: </span>{ticket.completeDescription}</div>}
                {ticket.resolvedDescription && <div className="text-xs text-gray-400 bg-gray-700/30 rounded px-3 py-2 border-l-2 border-teal-500"><span className="font-semibold text-teal-400">Resolved: </span>{ticket.resolvedDescription}</div>}
                {ticket.closeDescription    && <div className="text-xs text-gray-400 bg-gray-700/30 rounded px-3 py-2 border-l-2 border-gray-500"><span className="font-semibold text-gray-400">Closed: </span>{ticket.closeDescription}</div>}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-700 flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-gray-500">Created {timeAgo(ticket.createdAt)}</p>
          <button onClick={onClose} className="px-4 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition">Close</button>
        </div>
      </div>
    </div>
  );
};

// ─── Ticket Row — Facebook notification style ──────────────────────────────────
const TicketRow = ({ ticket, onClick }) => {
  const p = priorityOf(ticket.priority);
  const hasAttachment = !!ticket.attachment;

  // username ng requester — gamitin para sa profile pic
  const requesterUsername = ticket.userId || ticket.name || null;
  const isOpen = ticket.status === "Open";

  return (
    <div
      className={`group flex items-start gap-3 px-4 py-3 hover:bg-gray-700/50 transition-colors cursor-pointer border-b border-gray-700/40 last:border-0 relative ${isOpen ? "bg-indigo-500/5" : ""}`}
      onClick={onClick}
    >
      {/* ── Profile Picture (parang Facebook notification) ── */}
      <div className="relative flex-shrink-0 mt-0.5">
        <Avatar
          username={requesterUsername}
          isAdmin={false}
          size={42}
          initial={ticket.name || ticket.userId || "?"}
        />
        {/* Priority dot sa ibaba ng avatar */}
        <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-gray-800 ${p.dot}`} />
      </div>

      {/* ── Content ── */}
      <div className="flex-1 min-w-0">
        {/* Subject + attachment icon */}
        <div className="flex items-start gap-1">
          <p className="text-sm font-semibold text-white leading-snug group-hover:text-indigo-300 transition-colors flex-1">
            {ticket.name || ticket.userId || "User"}{" "}
            <span className="font-normal text-gray-300">
              submitted a ticket:{" "}
            </span>
            <span className="text-indigo-300 font-medium">
              {ticket.subject || "(No Subject)"}
            </span>
            {hasAttachment && (
              <span className="inline-flex ml-1 text-gray-400">
                <svg className="w-3.5 h-3.5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </span>
            )}
          </p>
        </div>

        {/* Badges row */}
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          <span className="text-[10px] text-gray-500 font-mono">{ticket.ticketNumber}</span>
          {ticket.status && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${statusOf(ticket.status)}`}>
              {ticket.status}
            </span>
          )}
          {ticket.priority && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${p.badge}`}>
              {ticket.priority}
            </span>
          )}
        </div>

        {/* Time */}
        <p className={`text-[11px] mt-1 font-medium ${isOpen ? "text-indigo-400" : "text-gray-500"}`}>
          {timeAgo(ticket.createdAt)}
        </p>
      </div>

      {/* ── Unread dot (parang FB) ── */}
      {isOpen && (
        <span className="flex-shrink-0 w-3 h-3 rounded-full bg-indigo-500 mt-2 self-center" />
      )}
    </div>
  );
};

// ─── MAIN NOTIFICATION ────────────────────────────────────────────────────────
const Notification = () => {
  const [open, setOpen]                     = useState(false);
  const [tickets, setTickets]               = useState([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [filter, setFilter]                 = useState("All");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const panelRef = useRef(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.API_BASE_URL}/Tickets`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTickets([...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (err) {
      setError("Failed to load tickets.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) fetchTickets(); }, [open, fetchTickets]);
  useEffect(() => {
    fetchTickets();
    const i = setInterval(fetchTickets, 60000);
    return () => clearInterval(i);
  }, [fetchTickets]);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const FILTERS = ["All", "Open", "Pending", "In Progress", "Resolved", "Closed"];
  const filtered = filter === "All" ? tickets : tickets.filter((t) => t.status === filter);
  const unreadCount = tickets.filter((t) => t.status === "Open").length;

  return (
    <>
      <div className="relative" ref={panelRef}>

        {/* Bell */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="p-2 hover:bg-gray-700 rounded-lg relative transition-colors"
          aria-label="Notifications"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11
                 a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341
                 C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595
                 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1
              bg-red-500 text-white text-[10px] font-bold rounded-full
              flex items-center justify-center leading-none animate-pulse">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {/* Panel */}
        {open && (
          <div className="absolute right-0 mt-2 w-[380px] max-w-[calc(100vw-1rem)]
            bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl z-50
            flex flex-col overflow-hidden"
            style={{ maxHeight: "560px" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 flex-shrink-0">
              <div>
                <h3 className="text-base font-bold text-white">Notifications</h3>
                {unreadCount > 0 && (
                  <p className="text-[11px] text-indigo-400 font-semibold">{unreadCount} unread</p>
                )}
              </div>
              <div className="flex gap-1">
                <button onClick={fetchTickets} title="Refresh"
                  className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <button onClick={() => setOpen(false)}
                  className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-1 px-3 py-2 border-b border-gray-700 overflow-x-auto flex-shrink-0">
              {FILTERS.map((f) => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`flex-shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors
                    ${filter === f ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white hover:bg-gray-700"}`}>
                  {f} ({f === "All" ? tickets.length : tickets.filter((t) => t.status === f).length})
                </button>
              ))}
            </div>

            {/* List */}
            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs text-gray-400">Loading tickets...</p>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2">
                  <p className="text-xs text-red-400">{error}</p>
                  <button onClick={fetchTickets} className="text-xs text-indigo-400 underline">Try again</button>
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <p className="text-xs text-gray-500">No tickets found</p>
                </div>
              ) : (
                filtered.map((ticket) => (
                  <TicketRow
                    key={ticket.ticketNumber || ticket.id}
                    ticket={ticket}
                    onClick={() => { setSelectedTicket(ticket); setOpen(false); }}
                  />
                ))
              )}
            </div>

            {filtered.length > 0 && (
              <div className="px-4 py-2.5 border-t border-gray-700 flex-shrink-0">
                <p className="text-[11px] text-gray-500 text-center">
                  {filtered.length} of {tickets.length} tickets • Click to view details
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedTicket && (
        <TicketModal ticket={selectedTicket} onClose={() => setSelectedTicket(null)} />
      )}
    </>
  );
};

export default Notification;